import { SearchTableActionRemoveLabelDialog } from '@aibees/elephant/components/search-table-action-remove-label-dialog';
import { useTableSelectionLabels } from '@aibees/elephant/components/search-table-action-remove-label-dialog/hooks/use-table-selection-labels';
import { useTableActionContext } from '@aibees/elephant/context/table-action/context';
import { StyledActionMenuItem } from '@aibees/elephant/feature/search-table-company/components/search-table-header/styles';
import { SearchModality } from '@aibees/elephant/feature/search-table-company/hooks/types';
import { useSearchModality } from '@aibees/elephant/hooks';
import { LabelType } from '@aibees/elephant/types/label';
import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Button, Menu } from '@mui/material';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { SearchTableActionAddLabelDialog } from './components/search-table-action-add-label-dialog/search-table-action-add-label-dialog';
import { AssignOwnerDialog } from './components/search-table-action-assign-owner-dialog';
import * as Styled from './styles';
import * as SX from './sx';
import type { SearchTableActionDialogMoreProps as Props } from './types';

export const SearchTableActionDialogMore = memo(
  ({ className, table, disabled, isSmall }: Props) => {
    const { labelDialogState, ownerDialogState, removeLabelDialogState, ids } =
      useTableActionContext();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { modality } = useSearchModality();
    const isNewTab = useMemo(
      () => modality === SearchModality.UNSAVED,
      [modality]
    );
    const [selectedLabels, isLoadingSelectedLabels] = useTableSelectionLabels(
      ids,
      LabelType.LABEL
    );
    const hasNoLabels = !isLoadingSelectedLabels && selectedLabels.length === 0;
    const open = Boolean(anchorEl);

    // Stable handlers using useCallback
    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
      },
      []
    );

    const handleClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleAssignOwnerDialog = useCallback(() => {
      ownerDialogState.onTrue();
      setAnchorEl(null);
    }, [ownerDialogState]);

    const handleShowAddTagDialog = useCallback(() => {
      labelDialogState.onTrue();
      setAnchorEl(null);
    }, [labelDialogState]);

    const handleShowRemoveTagDialog = useCallback(() => {
      removeLabelDialogState.onTrue();
      setAnchorEl(null);
    }, [removeLabelDialogState]);

    return (
      <Styled.Wrapper
        className={classnames('SearchTableActionDialogMore', className)}
        sx={SX.root}
        alignItems="center"
      >
        {/* Memoized dialogs, only mounted once */}
        <SearchTableActionAddLabelDialog />

        <SearchTableActionRemoveLabelDialog
          titleHeader={`Remove from Label (${ids.length})`}
          title="Labels to Remove:"
          icon="ic-outline-label-off"
          type={LabelType.LABEL}
          table={table}
        />
        <AssignOwnerDialog />

        <Button
          variant="outlined"
          size="small"
          id="basic-button"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          endIcon={
            <Icon name={open ? 'chevron-up' : 'chevron-down'} size={18} />
          }
          disabled={disabled}
          onClick={handleClick}
        >
          {!isSmall && 'More'}
        </Button>

        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{ 'aria-labelledby': 'basic-button' }}
        >
          <StyledActionMenuItem onClick={handleShowAddTagDialog}>
            Add Label
          </StyledActionMenuItem>
          <StyledActionMenuItem
            onClick={handleShowRemoveTagDialog}
            disabled={isNewTab || hasNoLabels}
          >
            Remove Label
          </StyledActionMenuItem>
          <StyledActionMenuItem onClick={handleAssignOwnerDialog}>
            Assign Owner
          </StyledActionMenuItem>
        </Menu>
      </Styled.Wrapper>
    );
  }
);

SearchTableActionDialogMore.displayName = 'SearchTableActionDialogMore';
